import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, WMSTileLayer, GeoJSON, LayersControl, Rectangle, Pane, useMap } from 'react-leaflet';
import { LinearProgress, Box } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import { IconButton } from '@mui/material';
import axios from 'axios';
import { debounce } from 'lodash';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import LegendMap from './map_legend_analy.js';
import { useMapSync } from './map_syncro_analy.js';
import zIndex from '@mui/material/styles/zIndex.js';

const MapRight = ({ cqlSevere, cqlFecha, mapRightLayer, weekNumber, issueTime, selectedModel, isInteractive = true }) => {
  const geoJsonRef = useRef(null);
  const mapRef = useRef(null);
  // Ref para el WMS
  const wmsRef = useRef(null);

  // Estado de carga
  const [loading, setLoading] = useState(true);
  // Estado para controlar la visibilidad de la leyenda
  const [showLegend, setShowLegend] = useState(false);

  // Función para mostrar u ocultar la leyenda
  const handleToggleLegend = () => {
    setShowLegend(!showLegend);
  };

  // Sincronización
  const { mapState, updateMapState } = useMapSync();

  // Función para resetear la vista del mapa
  const handleResetView = useCallback(() => {
    if (mapRef.current) {
      const map = mapRef.current;
      map.fitBounds([[73, -27], [33, 45]]);
    }
  }, []);

  // Función para mejorar la fluidez y renderización del mapa cuando el usuario mueva y haga zoom bruscamente
  const handleMoveEnd = debounce(() => {
    if (mapRef.current) {
      const map = mapRef.current;
      const center = map.getCenter();
      const zoom = map.getZoom();

      // Actualiza el estado del mapa en tu aplicación sincronizada
      updateMapState({
        center,
        zoom,
      });
    }
  }, 150); // ms de debounce

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Crear panes para controlar orden de capas
    map.createPane('geojsonPane');
    map.getPane('geojsonPane').style.zIndex = 400;
    map.createPane('wmsPane');
    map.getPane('wmsPane').style.zIndex = 450;
    map.createPane('otPane');
    map.getPane('otPane').style.zIndex = 500;

    map.on('moveend', handleMoveEnd);
    return () => map.off('moveend', handleMoveEnd);
  }, [handleMoveEnd]);

  // Bloquear interactividad si no está autenticado
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    ['dragging','scrollWheelZoom','boxZoom','keyboard','touchZoom']
      .forEach(fn => map[fn][isInteractive ? 'enable' : 'disable']());
  }, [isInteractive]);

  // Estilos memorizados para Rayos y OTs
  const LightningStyle = useMemo(() => ({
    radius: 0.50,
    fillColor: '#ff0000',
    color: '#ff0000',
    weight: 1,
    opacity: 1,
    fillOpacity: 0.8,
  }), []);

  const OT_Style = useMemo(() => ({
    radius: 3.0,
    fillColor: '#800080',
    color: '#800080',
    weight: 1,
    opacity: 1,
    fillOpacity: 0.8,
    zIndex: 1000,
  }), []);

  // Función de estilo memorizada para RDT
  const styleFeature = useMemo(() => {
    return (feature, layerOrder) => {
      const risk = feature.properties.SEVERITY;
      const opacityLevels = [0.65, 0.65, 0.65, 0.65, 0.65];
      const opacity = opacityLevels[layerOrder % opacityLevels.length];

      switch (risk) {
        case 'Severe':
          return {
            color: '#000000',
            fillColor: '#ffff00',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 30000
          };
        case 'Moderate':
          return {
            color: '#000000',
            fillColor: '#2E8B57',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 20000
          };
        case 'Light':
          return {
            color: '#000000',
            fillColor: '#2E8B57',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 10000
          };
        default:
          return {
            color: '#000000',
            fillColor: '#FFFFFF',
            fillOpacity: 0,
            opacity: opacity,
            weight: 0,
            zIndex: 1
          };
      }
    };
  }, []);

  // Callback para cada feature
  const onEachFeature = useCallback((feature, layer) => {
    if (feature.geometry.type === 'Point') {
      if (feature.properties.type === 'lightning') {
        layer.setStyle(LightningStyle);
        layer.bindPopup(`Lightning registered at: ${feature.properties.VALIDDATE}`);
      } else if (feature.properties.type === 'ot') {
        layer.setStyle(OT_Style);
        layer.once('add', () => layer.bringToFront());
        layer.bindPopup(`Overshoot registered at: ${feature.properties.ANALYSISTIME}`);
      }
    } else if (feature.geometry.type === 'Polygon') {
      layer.setStyle(styleFeature(feature, 0));
    }
  }, [LightningStyle, OT_Style, styleFeature]);

  // Función para representar puntos en el mapa
  const pointToLayer = useCallback((feature, latlng) => {
    if (feature.properties.type === 'lightning') {
      return L.circleMarker(latlng, LightningStyle);
    } else if (feature.properties.type === 'ot') {
      return L.circleMarker(latlng, {...OT_Style, pane: 'otPane'});
    }
  }, [LightningStyle, OT_Style]);

  // URLs GeoJSON para RDTs y OTs
  const geoJsonUrls = useMemo(() => ({
    // RDTs
    "TARGETS_RDT:rdt_EU_025_view": {
      primary: `${process.env.REACT_APP_API_URL}/proxy/wfs/targets_rdt?service=WFS&version=1.1.0&request=GetFeature&typeName=TARGETS_RDT:rdt_EU_025_view&outputFormat=application/json&CQL_FILTER=TIMESLOT='${cqlFecha}' AND WEEK_NUMBER='${weekNumber}'`,
      backup:  `${process.env.REACT_APP_API_URL}/proxy/wfs/targets_rdt_backup?service=WFS&version=1.1.0&request=GetFeature&typeName=TARGETS_RDT:rdt_EU_025_view&outputFormat=application/json&CQL_FILTER=TIMESLOT='${cqlFecha}' AND WEEK_NUMBER='${weekNumber}'`,
    },
    "TARGETS_RDT:rdt_EU_0125_view": {
      primary: `${process.env.REACT_APP_API_URL}/proxy/wfs/targets_rdt?service=WFS&version=1.1.0&request=GetFeature&typeName=TARGETS_RDT:rdt_EU_0125_view&outputFormat=application/json&CQL_FILTER=TIMESLOT='${cqlFecha}' AND WEEK_NUMBER='${weekNumber}'`,
      backup:  `${process.env.REACT_APP_API_URL}/proxy/wfs/targets_rdt_backup?service=WFS&version=1.1.0&request=GetFeature&typeName=TARGETS_RDT:rdt_EU_0125_view&outputFormat=application/json&CQL_FILTER=TIMESLOT='${cqlFecha}' AND WEEK_NUMBER='${weekNumber}'`,
    },
    // OTs
    "TARGETS_OT:ot_EU_025_view": {
      primary: `${process.env.REACT_APP_API_URL}/proxy/wfs/targets_ot?service=WFS&version=1.1.0&request=GetFeature&typeName=TARGETS_OT:ot_EU_025_view&outputFormat=application/json&CQL_FILTER=TIMESLOT='${cqlFecha}' AND WEEK_NUMBER='${weekNumber}'`,
      backup:  `${process.env.REACT_APP_API_URL}/proxy/wfs/targets_ot_backup?service=WFS&version=1.1.0&request=GetFeature&typeName=TARGETS_OT:ot_EU_025_view&outputFormat=application/json&CQL_FILTER=TIMESLOT='${cqlFecha}' AND WEEK_NUMBER='${weekNumber}'`,
    },
    "TARGETS_OT:ot_EU_0125_view": {
      primary: `${process.env.REACT_APP_API_URL}/proxy/wfs/targets_ot?service=WFS&version=1.1.0&request=GetFeature&typeName=TARGETS_OT:ot_EU_0125_view&outputFormat=application/json&CQL_FILTER=TIMESLOT='${cqlFecha}' AND WEEK_NUMBER='${weekNumber}'`,
      backup:  `${process.env.REACT_APP_API_URL}/proxy/wfs/targets_ot_backup?service=WFS&version=1.1.0&request=GetFeature&typeName=TARGETS_OT:ot_EU_0125_view&outputFormat=application/json&CQL_FILTER=TIMESLOT='${cqlFecha}' AND WEEK_NUMBER='${weekNumber}'`,
    }

  }), [cqlFecha, weekNumber]);

  // Función para realizar el fetch con fallback
  const fetchWithFallback = useCallback(async (urlObj, signal) => {
    if (!urlObj || !urlObj.primary || !urlObj.backup) return null;
    try {
      const resp = await axios.get(urlObj.primary, { signal });
      if (resp.status === 200) return resp.data;
    } catch (err) {
      console.warn(err.message);
    }
    const backupResp = await axios.get(urlObj.backup, { signal });
    return backupResp.data;
  }, []);

  const abortControllerRef = useRef(null);

  // useEffect para cargar RDTs y OTs via GeoJSON
  useEffect(() => {
    const fetchGeoJsonData = async (urls) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      try {
        const responses = await Promise.all(
          Object.entries(urls).map(async ([key, urlObj]) => {
            const data = await fetchWithFallback(urlObj, controller.signal);
            return { key, data };
          })
        );
        const allFeatures = responses.flatMap(({ key, data }) =>
          data.features.map(feature => {
            if (key.includes('TARGETS_OT')) {
              feature.properties.type = 'ot'; 
            }
            return feature;
          })
        );
        if (geoJsonRef.current) {
          requestAnimationFrame(() => {
            geoJsonRef.current.clearLayers();
            geoJsonRef.current.addData(allFeatures);
          });
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error al obtener datos GeoJSON:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchGeoJsonData(geoJsonUrls);

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [geoJsonUrls, fetchWithFallback]);

  // Rectángulos por capa
  const rectanguloByLayerName = useMemo(() => ({
    'TARGETS_RDT:rdt_EU_025_view':         [[33, -27], [73, 45]],
    'TARGETS_RDT:rdt_EU_0125_view':        [[36, -10], [60, 25]],
    'TARGETS_OT:ot_EU_025_view':           [[33, -27], [73, 45]],
    'TARGETS_OT:ot_EU_0125_view':          [[36, -10], [60, 25]],
    'TARGETS_LIGHTNINGS:lightnings_EU_025_view':  [[33, -27], [73, 45]],
    'TARGETS_LIGHTNINGS:lightnings_EU_0125_view': [[36, -10], [60, 25]],
  }), []);
  // Rectángulo de bounds para la capa seleccionada
  const rectBounds = useMemo(() => {
    if (!mapRightLayer) return null;
    // Si el nombre de la capa incluye “0125” → usa los bounds de 0125, sino los de 025
    return mapRightLayer.includes('0125')
      ? [[36, -10], [60, 25]]
      : [[33, -27], [73, 45]];
  }, [mapRightLayer]);

  // Ajustar los bounds del mapa al cargar la capa
  useEffect(() => {
    if (rectBounds && mapRef.current) {
      mapRef.current.fitBounds(rectBounds);
    }
  }, [rectBounds]);

  // Renderizador en Canvas
  const canvasRenderer = useMemo(() => L.canvas(), []);

  const layerKey = useMemo(() => {
    const is0125 = mapRightLayer.includes('_0125_');
    const suffix = is0125 ? '0125' : '025';
    return `lightnings_EU_${suffix}_view`;
  }, [mapRightLayer]);

  const lightningsLayer = useMemo(() => (
    <WMSTileLayer
      ref={wmsRef}
      url={`${process.env.REACT_APP_API_URL}/proxy/wms/targets_lightnings`}
      params={{
        service:     'WMS',
        request:     'GetMap',
        version:     '1.1.0',
        layers:      layerKey,
        format:      'image/png',
        transparent: true,
        tiled:     true,
        CQL_FILTER:  `VALIDDATE='${cqlFecha}' AND WEEK_NUMBER=${weekNumber}`,
      }}
      eventHandlers={{
        loading: () => setLoading(true),
        load:    () => setLoading(false),
      }}
      pane="wmsPane"
    />
  ), [layerKey, cqlFecha, weekNumber]);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {loading && (
        <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 1000 }}>
          <LinearProgress />
        </Box>
      )}
      <MapContainer
        ref={mapRef}
        center={[48, 7.5]}
        zoom={3.75}
        minZoom={2.65}
        maxZoom={16}
        style={{ height: "28vh", width: "100%" }}
        zoomSnap={0.35}
        wheelPxPerZoomLevel={100}
        zoomDelta={1.5}
        dragging={isInteractive}
        scrollWheelZoom={isInteractive}
        boxZoom={isInteractive}
        doubleClickZoom={isInteractive}
        touchZoom={isInteractive}
        keyboard={isInteractive}
      >
        <Pane name="geojsonPane" style={{ zIndex: 400 }} />
        <Pane name="wmsPane"     style={{ zIndex: 450 }} />
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="OpenStreetMap.Mapnik">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
              attribution=""
            />
          </LayersControl.BaseLayer>

          {/* Capa combinada GeoJSON (RDTs + OTs) */}
          <LayersControl.Overlay checked name="Combined GeoJSON Layer">
            <GeoJSON
              key={mapRightLayer}
              data={[]}
              ref={geoJsonRef}
              style={styleFeature}
              onEachFeature={onEachFeature}
              pointToLayer={pointToLayer}
              chunkedLoading
              renderer={canvasRenderer}
              pane="geojsonPane"
            />
          </LayersControl.Overlay>

          {/* Capa WMS para Rayos */}
          <LayersControl.Overlay checked name="Rayos (WMS)">
            {lightningsLayer}
          </LayersControl.Overlay>

          {/* Rectángulo de bounds */}
          {rectBounds && (
            <LayersControl.Overlay checked name="Bounds Rectangle">
              <Rectangle
                key={mapRightLayer}
                bounds={rectBounds}
                pathOptions={{ color: '#141414', weight: 1, dashArray: '3,5', fillOpacity: 0 }}
                interactive={false}
              />
            </LayersControl.Overlay>
          )}
        </LayersControl>

      </MapContainer>

      {/* Botón para resetear vista */}
      <IconButton
        onClick={handleResetView}
        sx={{ position: 'absolute', top: 0, right: 2.5, zIndex: 1000 }}
      >
        <HomeIcon fontSize="small" />
      </IconButton>

      {/* Botón para mostrar/ocultar la leyenda */}
      <IconButton
        onClick={handleToggleLegend}
        sx={{ position: 'absolute', bottom: 0, right: 2.5, zIndex: 1000 }}
      >
        <InfoIcon fontSize="small" />
      </IconButton>

      {/* Añadir la leyenda */}
      {showLegend && <LegendMap />}
    </Box>
  );
};

export default MapRight;
